import { ActionIcon, Badge, Group, Stack, Table, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconBrandWindows, IconMail, IconRefresh, IconTrash } from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import { isNonNullish } from 'remeda';
import { type ExternalMailboxConfiguration, useDeleteExternalMailboxConfiguration } from './api';
import { ExternalMailboxForm } from './ExternalMailboxForm';

type ExternalMailboxRowProps = {
  mailbox: ExternalMailboxConfiguration;
};

// A shared mailbox stores the owner and the shared address in a single backslash
// separated login.
const splitLogin = (login: string | null) => {
  const [address, sharedAddress] = (login ?? '').split('\\');
  return { address, sharedAddress };
};

export const ExternalMailboxRow = ({ mailbox }: ExternalMailboxRowProps) => {
  const { t } = useTranslation();
  const { deleteExternalMailboxConfiguration } = useDeleteExternalMailboxConfiguration();

  const { address, sharedAddress } = splitLogin(mailbox.login);
  const { host, port, ssl, isMicrosoft, reauthUri } = mailbox;

  const openEditModal = () =>
    modals.open({
      title: t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.EDIT),
      centered: true,
      size: 'lg',
      children: <ExternalMailboxForm mailbox={mailbox} onClose={modals.closeAll} />,
    });

  const confirmDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    modals.openConfirmModal({
      title: t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.DELETE),
      centered: true,
      children: <Text size="sm">{t(i18nKeys.AYS.DELETE_EXTERNAL_MAILBOX_CONFIGURATION)}</Text>,
      labels: { confirm: t(i18nKeys.DELETE), cancel: t(i18nKeys.CANCEL) },
      confirmProps: { color: 'red', leftSection: <IconTrash size={16} stroke={1.5} /> },
      onConfirm: () => deleteExternalMailboxConfiguration({ id: mailbox.id }),
    });
  };

  const reconnect = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isNonNullish(reauthUri)) window.location.replace(reauthUri);
  };

  return (
    <Table.Tr style={{ cursor: 'pointer' }} onClick={openEditModal}>
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon variant="light" color={isMicrosoft ? 'blue' : 'gray'} size={38} radius="md">
            {isMicrosoft ? (
              <IconBrandWindows size={20} stroke={1.5} />
            ) : (
              <IconMail size={20} stroke={1.5} />
            )}
          </ThemeIcon>
          <Stack gap={2} miw={0}>
            <Group gap="xs" wrap="nowrap">
              <Text size="sm" fw={500} truncate>
                {address}
              </Text>
              {isNonNullish(sharedAddress) && (
                <Badge size="xs" variant="light" color="grape">
                  {t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.SHARED)}
                </Badge>
              )}
            </Group>
            <Group gap={6} wrap="nowrap">
              <Text size="xs" c="dimmed" truncate>
                {isNonNullish(port) ? `${host}:${port}` : host}
              </Text>
              {ssl === true && (
                <Badge size="xs" variant="default" fw={400}>
                  {t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.SSL)}
                </Badge>
              )}
            </Group>
          </Stack>
        </Group>
      </Table.Td>
      <Table.Td w={100}>
        <Group gap={4} justify="flex-end" wrap="nowrap">
          {isNonNullish(reauthUri) && (
            <Tooltip label={t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.RECONNECT)}>
              <ActionIcon variant="subtle" color="gray" onClick={reconnect}>
                <IconRefresh size={18} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip label={t(i18nKeys.DELETE)}>
            <ActionIcon variant="subtle" color="red" onClick={confirmDelete}>
              <IconTrash size={18} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};
